import React from 'react';

/**
 * ArchiveOverwriteDialog — archiver UX §A4 / coding plan §4g.
 *
 * Shown before a contextual archive (survey / survey results / recipient) writes
 * into an archive file that already holds a copy of one of the selected
 * artifacts. The server overwrites the artifact in place — whole-instance,
 * delete-then-insert by primary key — so this dialog is the author-consent gate
 * required by archive_design.md §1a. It never uses window.confirm.
 *
 * The message names the artifacts (never their UUIDs, per alerts-messages.md)
 * and warns that a replace cannot be undone except by retrieving a prior archive
 * file from the file system.
 *
 * Render conditionally; the caller owns open/closed state and resolves its
 * promise from onConfirm / onCancel.
 *
 * @param {boolean}  open        - When false, nothing renders.
 * @param {Array}    collisions  - [{ id, type: 'survey'|'recipient'|'result', name }]
 * @param {string}   filename    - Name of the open archive file.
 * @param {Function} onConfirm   - Author agreed to replace.
 * @param {Function} onCancel    - Author declined (also Escape / backdrop).
 */
export default function ArchiveOverwriteDialog({
    open,
    collisions = [],
    filename = 'this archive',
    onConfirm,
    onCancel,
}) {
    React.useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') onCancel?.(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onCancel]);

    if (!open) return null;

    const typeLabel = { survey: 'Survey', recipient: 'Recipient', result: 'Results' };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onCancel}>
            <div style={{
                background: '#fff', borderRadius: 8, padding: 24, maxWidth: 460,
                boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>
                <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#3C4043' }}>
                    Replace archived copy?
                </h3>
                <div style={{ margin: '0 0 20px', fontSize: 14, color: '#5F6368', lineHeight: 1.5 }}>
                    <p style={{ margin: '0 0 8px' }}>
                        This will replace the copy already in <strong>{filename}</strong> of:
                    </p>
                    <ul style={{ margin: '0 0 12px', paddingLeft: 20 }}>
                        {collisions.map(c => (
                            <li key={`${c.type}:${c.id}`} style={{ marginBottom: 2 }}>
                                {c.name}
                                <span style={{ color: '#80868B' }}> ({typeLabel[c.type] || c.type})</span>
                            </li>
                        ))}
                    </ul>
                    <p style={{ margin: '0 0 8px' }}>The live originals are not affected.</p>
                    <p style={{ margin: 0, color: '#C5221F' }}>
                        This cannot be undone, except by retrieving a prior archive file from your
                        file system.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onCancel} style={{
                        padding: '6px 16px', border: '1px solid #DADCE0', borderRadius: 4,
                        background: '#fff', color: '#3C4043', cursor: 'pointer', fontSize: 13,
                    }}>Cancel</button>
                    <button onClick={onConfirm} autoFocus style={{
                        padding: '6px 16px', border: 'none', borderRadius: 4,
                        background: '#D32F2F', color: '#fff',
                        cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    }}>Replace</button>
                </div>
            </div>
        </div>
    );
}
